import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import FieldRow from "@/components/mobile/FieldRow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { getAvailableGrns } from "../api/validationQueries";
import { useUpdateInvoice } from "../api/invoiceMutations";
import { invoiceKeys } from "../api/queryKeys";
import { V2_COLORS } from "../constants/colors";
import { convertToINR, convertDate } from "@/helpers/Utils";
import { useToast } from "@/pages/InvoiceManagement/hooks/useToast";

interface GrnLinkModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  poCode: string;
  lifecycleVersion: number;
}

const GRID_COLUMNS = "40px 1fr 130px 130px";

export default function GrnLinkModal({
  open,
  onClose,
  invoiceId,
  poCode,
  lifecycleVersion,
}: GrnLinkModalProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const { showToast } = useToast();
  const isMobile = useIsMobile();

  const { data, isLoading, isError } = useQuery(
    invoiceKeys.grnAvailable(poCode),
    () => getAvailableGrns(poCode),
    { enabled: open && !!poCode },
  );

  const updateMutation = useUpdateInvoice(invoiceId);

  const filteredGrns = useMemo(() => {
    const items = data ?? [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((g) => String(g.grn_id).toLowerCase().includes(q));
  }, [data, search]);

  const toggleId = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleClose = () => {
    setSelectedIds([]);
    setSearch("");
    onClose();
  };

  const handleConfirm = () => {
    const allGrns = data ?? [];
    const grn_mappings = selectedIds.map((id) => {
      const grn = allGrns.find((g) => g.grn_id === id);
      return { grn_id: id, allocated_value: grn?.grn_value ?? 0 };
    });

    updateMutation.mutate(
      { lifecycle_version: lifecycleVersion, comments: "", grn_mappings },
      {
        onSuccess: () => {
          showToast({ type: "success", message: "GRN linked successfully" });
          handleClose();
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : "Failed to link GRN";
          showToast({ type: "error", message });
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: "16px", pb: 1 }}>
        Link GRN
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by GRN ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: V2_COLORS.textSecondary }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        )}

        {isError && (
          <Typography color={V2_COLORS.error} fontSize="13px">
            Failed to load available GRNs
          </Typography>
        )}

        {!isLoading && !isError && filteredGrns.length === 0 && (
          <Typography
            color={V2_COLORS.textSecondary}
            fontSize="13px"
            textAlign="center"
            py={4}
          >
            {search ? "No GRNs match your search" : "No GRNs available for this PO"}
          </Typography>
        )}

        {!isLoading && !isError && filteredGrns.length > 0 && (
          <Box border={`1px solid ${V2_COLORS.border}`} borderRadius="4px">
            {/* Table header — desktop only; mobile rows are self-labelled */}
            {!isMobile && (
              <>
                <Box
                  display="grid"
                  gridTemplateColumns={GRID_COLUMNS}
                  alignItems="center"
                  px={1.5}
                  py={1}
                  sx={{ bgcolor: V2_COLORS.bgMuted }}
                >
                  <Box />
                  <Typography fontSize="11px" fontWeight={700} color={V2_COLORS.textSecondary} letterSpacing="0.5px">
                    GRN ID
                  </Typography>
                  <Typography fontSize="11px" fontWeight={700} color={V2_COLORS.textSecondary} letterSpacing="0.5px" textAlign="right">
                    GRN VALUE
                  </Typography>
                  <Typography fontSize="11px" fontWeight={700} color={V2_COLORS.textSecondary} letterSpacing="0.5px" textAlign="right">
                    GRN DATE
                  </Typography>
                </Box>
                <Divider />
              </>
            )}

            {filteredGrns.map((grn, i) => {
              const selected = selectedIds.includes(grn.grn_id);
              const dateLabel = grn.grn_date ? convertDate(grn.grn_date) : "—";
              return (
                <Box key={grn.grn_id}>
                  {i > 0 && <Divider />}
                  <Box
                    display={isMobile ? "flex" : "grid"}
                    gridTemplateColumns={isMobile ? undefined : GRID_COLUMNS}
                    gap={isMobile ? 1.5 : 0}
                    alignItems="center"
                    px={1.5}
                    py={1.25}
                    onClick={() => toggleId(grn.grn_id)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: selected ? V2_COLORS.primaryBg : "transparent",
                      "&:hover": { bgcolor: V2_COLORS.primaryBg },
                    }}
                  >
                    <Checkbox
                      checked={selected}
                      size="small"
                      sx={{ p: 0 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleId(grn.grn_id)}
                    />
                    {isMobile ? (
                      <Box display="flex" flexDirection="column" gap={0.25} minWidth={0}>
                        <Typography fontSize="13px" fontWeight={500}>
                          {grn.grn_id}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" columnGap={1.5}>
                          <FieldRow inline label="Value" value={convertToINR(grn.grn_value)} />
                          <FieldRow inline label="Date" value={dateLabel} />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Typography fontSize="13px" fontWeight={500}>
                          {grn.grn_id}
                        </Typography>
                        <Typography fontSize="13px" textAlign="right">
                          {convertToINR(grn.grn_value)}
                        </Typography>
                        <Typography fontSize="13px" textAlign="right" color={V2_COLORS.textSecondary}>
                          {dateLabel}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {selectedIds.length > 0 && (
          <Typography fontSize="12px" color={V2_COLORS.textSecondary} mt={1.5}>
            {selectedIds.length} GRN{selectedIds.length > 1 ? "s" : ""} selected
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1, ...(isMobile && { "& > *": { flex: 1 } }) }}>
        <Button
          onClick={handleClose}
          disabled={updateMutation.isLoading}
          sx={{ textTransform: "none", color: V2_COLORS.textSecondary }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={selectedIds.length === 0 || updateMutation.isLoading}
          onClick={handleConfirm}
          startIcon={updateMutation.isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : undefined}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            backgroundColor: V2_COLORS.primary,
            "&:hover": { backgroundColor: V2_COLORS.primaryHover, boxShadow: "none" },
          }}
        >
          Link GRN
        </Button>
      </DialogActions>
    </Dialog>
  );
}
